import { RefObject, useLayoutEffect, useRef } from 'react'
import { IPosition, IPositionType } from 'src/generated-types'
import { PositionConfig } from 'src/generated-types/configure-positions'
import { ConfigurePalette } from './configurePalette'
import { paintDrawing, paintHighlight } from './drawingSvgPainter'

interface UseDrawingSvgPaintOptions {
  hostRef: RefObject<HTMLDivElement>
  svg: string | undefined
  positions: IPosition[]
  cages: IPosition[]
  activeType: IPositionType
  selectedIds: Set<number>
  configById: Map<number, PositionConfig>
  highlightedIds: Set<number>
  palette: ConfigurePalette
}

export const useDrawingSvgPaint = ({
  hostRef,
  svg,
  positions,
  cages,
  activeType,
  selectedIds,
  configById,
  highlightedIds,
  palette,
}: UseDrawingSvgPaintOptions) => {
  const positionShapesRef = useRef<Map<number, SVGGraphicsElement>>(new Map())
  const cageShapesRef = useRef<Map<number, SVGGraphicsElement>>(new Map())
  const marqueeRef = useRef<SVGRectElement | null>(null)
  const hoveredIdRef = useRef<string | null>(null)

  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host || !svg) return
    host.innerHTML = svg
    const svgElement = host.querySelector('svg')
    if (!svgElement) return
    const painted = paintDrawing(svgElement, { positions, cages, activeType, selectedIds, configById, palette })
    positionShapesRef.current = painted.positionShapes
    cageShapesRef.current = painted.cageShapes
    marqueeRef.current = painted.marquee
    hoveredIdRef.current = null
  }, [hostRef, svg, positions, cages, activeType, selectedIds, configById, palette])

  useLayoutEffect(() => {
    const host = hostRef.current
    if (!host || !svg) return
    paintHighlight(host, { positions, cages, activeType, selectedIds, configById, palette, highlightedIds })
  }, [hostRef, svg, positions, cages, activeType, selectedIds, configById, palette, highlightedIds])

  return { positionShapesRef, cageShapesRef, marqueeRef, hoveredIdRef }
}
